import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import InputAdornment from '@mui/material/InputAdornment';
import ListSubheader from '@mui/material/ListSubheader';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import { alpha } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { FormField } from 'components/forms';
import { useFeedback } from 'contexts/FeedbackContext';
import useIsAdmin from 'hooks/useIsAdmin';
import moment, { type Moment } from 'moment';
import { forwardRef, type ReactNode, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import {
  useDeleteBusinessUnitTemplateMutation,
  useGetBusinessUnitTemplatesQuery,
  useRequestTemplateDeletionMutation,
} from 'services/api/businessUnits.api';
import type { SendTemplate } from 'services/types/message';
import {
  GLASS_BACKGROUND,
  GLASS_BLUR,
  NAVY,
  RADIUS_CARD,
  RADIUS_COIN,
  RADIUS_ITEM,
  SHADOW,
  SURFACE_CONTAINER_LOW,
  SURFACE_CONTAINER_LOWEST,
  YELLOW,
} from 'styles/tokens';

import buildTemplateComponents from './buildTemplateComponents';
import buildTemplateMessage from './buildTemplateMessage';
import TemplatePreview from './TemplatePreview';
import TemplateStatusTag, { isTemplateSelectable } from './templateStatus';
import type { Parameters, Template } from './types';
import useTemplateParameters from './useTemplateParameters';

const SECTION_LABEL: Record<keyof Parameters, string> = {
  BODY: 'Variables del mensaje',
  HEADER: 'Encabezado',
  FOOTER: 'Pie',
  BUTTON: 'Botones',
};

export interface TemplateComposerHandle {
  /** Returns the payload for the selected template, or null when nothing is selected. */
  getSubmitData: () => {
    templateName: string;
    components: SendTemplate['components'];
    aiStatus: 'enabled' | 'disabled' | null;
    postAt: string | null;
  } | null;
}

interface Props {
  businessUnitId: number;
  /** Maps a template named-parameter to its prefill value (client- or form-derived). */
  prefill: (paramName: string) => string;
  /** Show the "+ Crear plantilla" toggle. The create form itself is provided via `createTemplateSlot`. */
  allowTemplateCreation?: boolean;
  createTemplateSlot?: ReactNode;
  /** Show the "Borrar plantilla" action next to the preview. */
  showDelete?: boolean;
  /** Only templates passing this filter are listed (defaults to all). Non-approved ones are disabled. */
  templateFilter?: (template: Template) => boolean;
  /** Auto-select the most recent approved template on load. */
  autoSelectFirst?: boolean;
  /** Preselect a template by name when it loads. */
  initialTemplateName?: string | null;
  /** Show the "Programar envío" control. */
  showSchedule?: boolean;
  /** AI toggle config; `null` hides it and forces aiStatus to null. */
  ai?: { initial: 'enabled' | 'disabled' | null } | null;
  /** Render the composer's own submit button at the bottom of the left column. */
  showSubmitButton?: boolean;
  /**
   * When set, the compose/preview panels use this min height (px) and the layout grows so the
   * PARENT scrolls — instead of the fixed-height, internally-scrolling layout used inside a
   * bounded-height modal. Use it when the composer lives in a normally-scrolling form.
   */
  panelMinHeight?: number;
  sendLabel?: string;
  scheduleLabel?: string;
  /** Called when the internal form is submitted (Enter or the composer's submit button). */
  onSubmit?: () => void;
  onCanSendChange?: (canSend: boolean) => void;
  onCreatingChange?: (creating: boolean) => void;
  /** Called after a non-admin deletion request so the parent can close the modal. */
  onAfterDeleteRequest?: () => void;
}

/**
 * Shared template composer: template selector (searchable) + variables editor + optional
 * schedule/AI controls + live preview + optional create/delete flows. Owns all template state
 * and exposes the submit payload via a ref so each caller keeps its own submit logic.
 */
const TemplateComposer = forwardRef<TemplateComposerHandle, Props>(
  (
    {
      businessUnitId,
      prefill,
      allowTemplateCreation = false,
      createTemplateSlot,
      showDelete = false,
      templateFilter,
      autoSelectFirst = false,
      initialTemplateName,
      showSchedule = false,
      ai = null,
      showSubmitButton = false,
      panelMinHeight,
      sendLabel = 'Enviar plantilla',
      scheduleLabel = 'Programar plantilla',
      onSubmit,
      onCanSendChange,
      onCreatingChange,
      onAfterDeleteRequest,
    },
    ref
  ) => {
    const { data: templates, isLoading: isLoadingTemplates } = useGetBusinessUnitTemplatesQuery(businessUnitId);
    const isAdmin = useIsAdmin();
    const { notifySaved } = useFeedback();
    const [deleteTemplate, { isLoading: isDeleting }] = useDeleteBusinessUnitTemplateMutation();
    const [requestTemplateDeletion, { isLoading: isRequestingDeletion }] = useRequestTemplateDeletionMutation();

    const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null);
    const { parameters, setParamValue, isComplete } = useTemplateParameters(selectedTemplate, prefill);
    const [activeTab, setActiveTab] = useState(0);
    const [templateSearch, setTemplateSearch] = useState('');
    const [isTemplateSelectOpen, setIsTemplateSelectOpen] = useState(false);
    const templateSearchInputRef = useRef<HTMLInputElement>(null);
    const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState(false);

    const [schedule, setSchedule] = useState(false);
    const [postAt, setPostAt] = useState<Moment | null>(() => {
      const d = moment().add(15, 'minutes').startOf('minute');
      // Round up to the next 5-minute step to match the picker's minutesStep.
      d.minute(Math.ceil(d.minute() / 5) * 5)
        .second(0)
        .millisecond(0);
      return d;
    });
    const [scheduleDateError, setScheduleDateError] = useState<string | null>(null);
    const [scheduleDatePickerOpen, setScheduleDatePickerOpen] = useState(false);
    const [aiStatus, setAiStatus] = useState<'enabled' | 'disabled' | null>(ai ? ai.initial : null);

    const listedTemplates = useMemo(
      () => (templates || []).filter((t) => (templateFilter ? templateFilter(t) : true)),
      [templates, templateFilter]
    );

    const message = useMemo(() => buildTemplateMessage(selectedTemplate, parameters), [selectedTemplate, parameters]);

    const scheduleValid = !schedule || (!scheduleDateError && !!postAt?.isValid());
    const canSend = !!selectedTemplate && isComplete && scheduleValid;
    // Grow-and-let-the-parent-scroll layout vs the fixed-height, internally-scrolling one.
    const isScroll = panelMinHeight != null;

    // Preselect by name (e.g. quick-templates).
    useEffect(() => {
      if (!initialTemplateName || !templates) return;
      const found = templates.find((t) => t.name === initialTemplateName);
      if (found) setSelectedTemplate(found);
    }, [initialTemplateName, templates]);

    // Auto-select the most recent approved template.
    useEffect(() => {
      if (!autoSelectFirst || !templates || templates.length === 0 || selectedTemplate) return;
      const approved = templates.filter((t) => ['approved', 'reinstated'].includes(t.status?.toLowerCase()));
      if (approved.length === 0) return;
      approved.sort((a, b) => {
        const sa = a.status?.toLowerCase();
        const sb = b.status?.toLowerCase();
        if (sa === 'approved' && sb !== 'approved') return -1;
        if (sa !== 'approved' && sb === 'approved') return 1;
        return new Date(b.modified_at).getTime() - new Date(a.modified_at).getTime();
      });
      setSelectedTemplate(approved[0]);
    }, [autoSelectFirst, templates, selectedTemplate]);

    // Never leave the user stuck on the create tab if they lose the permission.
    useEffect(() => {
      if (!allowTemplateCreation && activeTab !== 0) setActiveTab(0);
    }, [allowTemplateCreation, activeTab]);

    useEffect(() => {
      onCanSendChange?.(canSend);
    }, [canSend, onCanSendChange]);

    useEffect(() => {
      onCreatingChange?.(activeTab === 1);
    }, [activeTab, onCreatingChange]);

    useImperativeHandle(
      ref,
      () => ({
        getSubmitData: () => {
          if (!selectedTemplate) return null;
          return {
            templateName: selectedTemplate.name,
            components: buildTemplateComponents(parameters),
            aiStatus: ai ? aiStatus : null,
            postAt: schedule && postAt ? postAt.toISOString() : null,
          };
        },
      }),
      [selectedTemplate, parameters, ai, aiStatus, schedule, postAt]
    );

    const handleConfirmDelete = () => {
      if (!selectedTemplate) return;
      if (isAdmin) {
        setIsConfirmDeleteOpen(false);
        deleteTemplate({ businessUnitId, name: selectedTemplate.name })
          .unwrap()
          .then(() => {
            notifySaved('Plantilla eliminada');
            setTimeout(() => window.location.reload(), 1500);
          })
          .catch((err: unknown) => console.error(err));
      } else {
        requestTemplateDeletion({ businessUnitId, templateName: selectedTemplate.name })
          .unwrap()
          .then(() => {
            setIsConfirmDeleteOpen(false);
            notifySaved('Solicitud enviada');
            onAfterDeleteRequest?.();
          })
          .catch((err: unknown) => {
            console.error('Error requesting template deletion:', err);
            setIsConfirmDeleteOpen(false);
            notifySaved('Solicitud enviada');
            onAfterDeleteRequest?.();
          });
      }
    };

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
        {allowTemplateCreation && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1.5 }}>
            {activeTab === 0 ? (
              <Button
                onClick={() => setActiveTab(1)}
                sx={{
                  color: '#fff',
                  backgroundColor: NAVY,
                  borderRadius: RADIUS_COIN,
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 2,
                  boxShadow: 'none',
                  '&:hover': { backgroundColor: NAVY, opacity: 0.92, boxShadow: SHADOW },
                }}
              >
                + Crear plantilla
              </Button>
            ) : (
              <Button
                onClick={() => setActiveTab(0)}
                sx={{
                  color: NAVY,
                  borderRadius: RADIUS_COIN,
                  textTransform: 'none',
                  fontWeight: 500,
                  px: 2,
                  '&:hover': { backgroundColor: alpha(NAVY, 0.06) },
                }}
              >
                ← Volver a enviar plantilla
              </Button>
            )}
          </Box>
        )}

        {activeTab === 1 && allowTemplateCreation ? (
          <Box sx={isScroll ? {} : { flex: 1, minHeight: 0, overflow: 'auto' }}>{createTemplateSlot}</Box>
        ) : (
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              gap: 3,
              ...(isScroll
                ? { alignItems: 'flex-start', overflow: 'visible' }
                : { flex: 1, minHeight: 0, overflow: { xs: 'auto', md: 'hidden' } }),
            }}
          >
            <Box
              component="form"
              onSubmit={(e) => {
                e.preventDefault();
                if (canSend) onSubmit?.();
              }}
              sx={{
                flex: 1,
                minWidth: { xs: '100%', md: '250px' },
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
              }}
            >
              <FormField
                label="Seleccionar plantilla"
                value={selectedTemplate?.name || ''}
                onChange={(e) => {
                  const target = templates?.find((t) => t.name === e.target.value);
                  if (target) setSelectedTemplate(target);
                }}
                disabled={isLoadingTemplates}
                fullWidth
                select
                placeholder={isLoadingTemplates ? 'Cargando plantillas…' : 'Selecciona una plantilla'}
                sx={{ mb: 2 }}
                slotProps={{
                  select: {
                    open: isTemplateSelectOpen,
                    onOpen: () => setIsTemplateSelectOpen(true),
                    onClose: () => {
                      setIsTemplateSelectOpen(false);
                      setTemplateSearch('');
                    },
                    displayEmpty: true,
                    renderValue: (value) =>
                      value ? (
                        <Typography component="span" sx={{ color: NAVY, fontSize: '0.9375rem' }}>
                          {value as string}
                        </Typography>
                      ) : (
                        <Typography component="span" sx={{ color: alpha(NAVY, 0.35), fontSize: '0.9375rem' }}>
                          {isLoadingTemplates ? 'Cargando plantillas…' : 'Selecciona una plantilla'}
                        </Typography>
                      ),
                    MenuProps: {
                      slotProps: {
                        transition: { onEntered: () => templateSearchInputRef.current?.focus() },
                        list: { autoFocusItem: false },
                        paper: {
                          sx: {
                            backgroundColor: GLASS_BACKGROUND,
                            backdropFilter: GLASS_BLUR,
                            borderRadius: RADIUS_CARD,
                            boxShadow: SHADOW,
                            border: `1px solid ${alpha(NAVY, 0.06)}`,
                            mt: 0.5,
                            maxHeight: 360,
                            '& .MuiMenuItem-root': {
                              color: NAVY,
                              fontSize: '0.9rem',
                              borderRadius: RADIUS_ITEM,
                              mx: 0.75,
                              my: 0.25,
                              transition: 'background-color 0.12s ease',
                              '&:hover': { backgroundColor: alpha(NAVY, 0.06) },
                              '&.Mui-selected': {
                                backgroundColor: alpha(YELLOW, 0.18),
                                '&:hover': { backgroundColor: alpha(YELLOW, 0.24) },
                              },
                              '&.Mui-disabled': { opacity: 0.45 },
                            },
                          },
                        },
                      },
                    },
                  },
                  input: {
                    endAdornment: isLoadingTemplates ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', pr: 4 }}>
                        <CircularProgress size={18} sx={{ color: alpha(NAVY, 0.5) }} />
                      </Box>
                    ) : undefined,
                  },
                }}
              >
                <ListSubheader
                  sx={{ p: 1, backgroundColor: 'transparent', position: 'sticky', top: 0, zIndex: 1 }}
                  onMouseDown={(e) => e.preventDefault()}
                >
                  <TextField
                    size="small"
                    fullWidth
                    placeholder="Buscar plantilla..."
                    value={templateSearch}
                    onChange={(e) => setTemplateSearch(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                        const items = Array.from(
                          document.querySelectorAll<HTMLElement>('[role="option"]:not([aria-disabled="true"])')
                        );
                        if (items.length > 0) {
                          e.preventDefault();
                          (e.key === 'ArrowDown' ? items[0] : items[items.length - 1]).focus();
                        }
                      } else if (e.key === 'Enter') {
                        const items = Array.from(
                          document.querySelectorAll<HTMLElement>('[role="option"]:not([aria-disabled="true"])')
                        );
                        if (items.length > 0) items[0].click();
                      } else if (e.key !== 'Escape') {
                        e.stopPropagation();
                      }
                    }}
                    inputRef={templateSearchInputRef}
                    slotProps={{
                      input: {
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon fontSize="small" sx={{ color: alpha(NAVY, 0.5) }} />
                          </InputAdornment>
                        ),
                      },
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: SURFACE_CONTAINER_LOW,
                        borderRadius: RADIUS_ITEM,
                        fontSize: '0.875rem',
                        color: NAVY,
                        '& fieldset': { border: 'none' },
                        '&:hover fieldset': { border: 'none' },
                        '&.Mui-focused fieldset': { border: 'none' },
                      },
                    }}
                  />
                </ListSubheader>
                {(() => {
                  const lower = templateSearch.toLowerCase();
                  const filtered = [...listedTemplates]
                    .filter((t) => !templateSearch || t.name.toLowerCase().includes(lower))
                    .sort((a, b) => a.name.localeCompare(b.name));
                  if (filtered.length === 0) {
                    return (
                      <Box sx={{ px: 2, py: 1.5, color: alpha(NAVY, 0.55), fontSize: '0.875rem' }}>
                        No hay plantillas que coincidan
                      </Box>
                    );
                  }
                  return filtered.map((template) => (
                    <MenuItem
                      key={template.id}
                      value={template.name}
                      disabled={!isTemplateSelectable(template.status)}
                      sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'space-between' }}
                    >
                      <span>{template.name}</span>
                      <TemplateStatusTag status={template.status} />
                    </MenuItem>
                  ));
                })()}
              </FormField>

              <Stack spacing={2} sx={{ flex: 1 }}>
                {(Object.keys(parameters) as (keyof Parameters)[]).map((key) =>
                  parameters[key].length > 0 ? (
                    <Box key={key}>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: 600,
                          mb: 1,
                          color: alpha(NAVY, 0.5),
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em',
                          fontSize: '0.7rem',
                        }}
                      >
                        {SECTION_LABEL[key]}
                      </Typography>
                      <Stack spacing={1.5}>
                        {parameters[key].map((param, index) => {
                          const bodyComponent = selectedTemplate?.components.find((c) => c.type === 'BODY');
                          const placeholder =
                            bodyComponent?.example?.body_text?.[0]?.[index] ||
                            bodyComponent?.example?.body_text_named_params?.[index]?.example ||
                            '';
                          const variableLabel =
                            param.parameter_name ||
                            bodyComponent?.example?.body_text_named_params?.[index]?.param_name ||
                            `Variable ${index + 1}`;
                          return (
                            <FormField
                              key={`${key}-${index.toString()}`}
                              label={variableLabel}
                              value={param.text}
                              onChange={(e) => setParamValue(key, index, e.target.value)}
                              fullWidth
                              placeholder={placeholder}
                            />
                          );
                        })}
                      </Stack>
                    </Box>
                  ) : null
                )}

                {showSchedule && (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={schedule}
                        onChange={(e) => {
                          setSchedule(e.target.checked);
                          if (!e.target.checked) setScheduleDateError(null);
                        }}
                        sx={{
                          color: alpha(NAVY, 0.4),
                          borderRadius: RADIUS_ITEM,
                          '&.Mui-checked': { color: NAVY },
                          '&:hover': { backgroundColor: alpha(NAVY, 0.04) },
                        }}
                      />
                    }
                    label="Programar envío"
                    sx={{ m: 0, '& .MuiFormControlLabel-label': { color: NAVY, fontSize: '0.9rem', fontWeight: 500 } }}
                  />
                )}
                {showSchedule && schedule && (
                  <Box>
                    <Typography
                      component="label"
                      sx={{
                        fontFamily: 'Jost, sans-serif',
                        fontSize: '0.8125rem',
                        fontWeight: 500,
                        color: alpha(NAVY, 0.7),
                        mb: 0.875,
                        display: 'block',
                        letterSpacing: '0.005em',
                      }}
                    >
                      Fecha y hora de envío
                    </Typography>
                    <LocalizationProvider
                      dateAdapter={AdapterMoment}
                      localeText={{
                        okButtonLabel: 'Confirmar',
                        cancelButtonLabel: 'Cancelar',
                        datePickerToolbarTitle: 'Selecciona fecha y hora',
                      }}
                    >
                      <DateTimePicker
                        ampm={false}
                        minutesStep={5}
                        format="DD/MM/YYYY HH:mm"
                        value={postAt}
                        onChange={(date) => setPostAt(date)}
                        minDateTime={moment()}
                        open={scheduleDatePickerOpen}
                        onOpen={() => setScheduleDatePickerOpen(true)}
                        onClose={() => setScheduleDatePickerOpen(false)}
                        onError={(reason) => {
                          const minLabel = moment().format('DD/MM/YYYY HH:mm');
                          if (reason === 'minDate' || reason === 'minTime' || reason === 'disablePast') {
                            setScheduleDateError(`La fecha y hora deben ser posteriores a ${minLabel}.`);
                          } else if (reason === 'invalidDate') {
                            setScheduleDateError('Formato inválido. Usa DD/MM/AAAA HH:mm.');
                          } else if (
                            reason === 'shouldDisableMonth' ||
                            reason === 'shouldDisableYear' ||
                            reason === 'shouldDisableTime-hours' ||
                            reason === 'shouldDisableTime-minutes' ||
                            reason === 'shouldDisableTime-seconds'
                          ) {
                            setScheduleDateError('Esa fecha u hora no está disponible. Elige otra.');
                          } else if (reason) {
                            setScheduleDateError(`La fecha y hora deben ser posteriores a ${minLabel}.`);
                          } else {
                            setScheduleDateError(null);
                          }
                        }}
                        slotProps={{
                          inputAdornment: { sx: { display: 'none' } },
                          openPickerButton: { sx: { display: 'none' } },
                          day: {
                            sx: {
                              '&&.Mui-selected': {
                                backgroundColor: NAVY,
                                color: '#fff',
                                fontWeight: 700,
                                border: 'none',
                                '&:hover, &:focus': { backgroundColor: NAVY, opacity: 0.92 },
                              },
                            },
                          },
                          textField: {
                            fullWidth: true,
                            size: 'small',
                            error: !!scheduleDateError,
                            helperText: scheduleDateError ?? undefined,
                            onClick: () => setScheduleDatePickerOpen(true),
                            slotProps: { input: { readOnly: true } },
                            sx: {
                              fontFamily: 'Jost, sans-serif',
                              cursor: 'pointer',
                              '& .MuiInputBase-root, & .MuiPickersInputBase-root, & .MuiPickersOutlinedInput-root': {
                                fontFamily: 'Jost, sans-serif',
                                borderRadius: RADIUS_ITEM,
                                bgcolor: SURFACE_CONTAINER_LOWEST,
                                boxShadow: `inset 0 0 0 1px ${alpha(NAVY, 0.18)}`,
                                transition: 'box-shadow 0.18s ease, background-color 0.18s ease',
                                color: NAVY,
                                fontSize: '0.9375rem',
                                py: 0.4,
                                px: 0.75,
                                cursor: 'pointer',
                              },
                              '& .MuiInputBase-input, & .MuiPickersInputBase-sectionsContainer, & .MuiPickersSectionList-root':
                                {
                                  cursor: 'pointer',
                                },
                              '& fieldset, & .MuiPickersOutlinedInput-notchedOutline, & .MuiOutlinedInput-notchedOutline':
                                {
                                  border: 'none !important',
                                },
                              '& .MuiInputBase-root:hover, & .MuiPickersInputBase-root:hover, & .MuiPickersOutlinedInput-root:hover':
                                {
                                  boxShadow: `inset 0 0 0 1px ${alpha(NAVY, 0.32)}`,
                                  bgcolor: SURFACE_CONTAINER_LOWEST,
                                },
                              '& .MuiInputBase-root.Mui-focused, & .MuiPickersInputBase-root.Mui-focused, & .MuiPickersOutlinedInput-root.Mui-focused':
                                {
                                  boxShadow: `inset 0 0 0 1.5px ${NAVY}, 0 0 0 4px ${alpha(YELLOW, 0.25)}`,
                                },
                              '& .MuiInputBase-root.Mui-error, & .MuiPickersInputBase-root.Mui-error, & .MuiPickersOutlinedInput-root.Mui-error':
                                {
                                  boxShadow: `inset 0 0 0 1.5px #E7233B, 0 0 0 4px ${alpha('#E7233B', 0.18)}`,
                                },
                              '& .MuiInputBase-input, & .MuiPickersInputBase-sectionsContainer': {
                                color: NAVY,
                                fontSize: '0.9375rem',
                                py: 1,
                                px: 1,
                              },
                              '& .MuiPickersSectionList-section, & .MuiPickersSectionList-sectionContent': {
                                color: NAVY,
                                fontSize: '0.9375rem',
                              },
                              '& .MuiInputAdornment-root .MuiIconButton-root': {
                                color: alpha(NAVY, 0.55),
                                '&:hover': { color: NAVY, backgroundColor: alpha(NAVY, 0.06) },
                              },
                              '& .MuiFormHelperText-root': {
                                mx: 0,
                                mt: 0.625,
                                fontSize: '0.75rem',
                                fontFamily: 'Jost, sans-serif',
                                color: alpha(NAVY, 0.55),
                                '&.Mui-error': { color: '#E7233B' },
                              },
                            },
                          },
                          popper: {
                            sx: {
                              '& .MuiPaper-root': {
                                backgroundColor: GLASS_BACKGROUND,
                                backdropFilter: GLASS_BLUR,
                                borderRadius: RADIUS_CARD,
                                boxShadow: SHADOW,
                                border: `1px solid ${alpha(NAVY, 0.06)}`,
                                mt: 0.5,
                                fontFamily: 'Jost, sans-serif',
                                overflow: 'hidden',
                              },
                              '& .MuiPickersCalendarHeader-label, & .MuiPickersCalendarHeader-switchViewIcon': {
                                color: NAVY,
                                fontWeight: 600,
                                textTransform: 'capitalize',
                              },
                              '& .MuiPickersArrowSwitcher-root .MuiIconButton-root': {
                                color: alpha(NAVY, 0.6),
                                borderRadius: RADIUS_ITEM,
                                '&:hover': { backgroundColor: alpha(NAVY, 0.06), color: NAVY },
                              },
                              '& .MuiDayCalendar-weekDayLabel': {
                                color: alpha(NAVY, 0.45),
                                fontWeight: 600,
                                fontSize: '0.75rem',
                              },
                              '& .MuiPickersDay-root': { color: NAVY, fontWeight: 500, borderRadius: '50%' },
                              '& .MuiPickersDay-root:hover': { backgroundColor: alpha(NAVY, 0.06) },
                              '& .MuiPickersDay-root.Mui-disabled': { color: alpha(NAVY, 0.25) },
                              '& .MuiPickersDay-root.MuiPickersDay-today:not(.Mui-selected)': {
                                border: `1.5px solid ${alpha(NAVY, 0.45)} !important`,
                                backgroundColor: 'transparent !important',
                                color: `${NAVY} !important`,
                              },
                              '& .MuiPickersDay-root.Mui-selected, & .MuiPickersDay-root.Mui-selected:focus': {
                                backgroundColor: `${NAVY} !important`,
                                color: '#fff !important',
                                fontWeight: 700,
                                border: 'none',
                              },
                              '& .MuiPickersDay-root.Mui-selected:hover': {
                                backgroundColor: `${NAVY} !important`,
                                opacity: 0.92,
                              },
                              '& .MuiPickersYear-yearButton': { color: NAVY, borderRadius: RADIUS_ITEM },
                              '& .MuiPickersYear-yearButton:hover': { backgroundColor: alpha(NAVY, 0.06) },
                              '& .MuiPickersYear-yearButton.Mui-selected, & .MuiPickersYear-yearButton.Mui-selected:focus':
                                {
                                  backgroundColor: `${NAVY} !important`,
                                  color: '#fff !important',
                                  fontWeight: 700,
                                },
                              '& .MuiPickersYear-yearButton.Mui-selected:hover': {
                                backgroundColor: `${NAVY} !important`,
                                opacity: 0.92,
                              },
                              '& .MuiMultiSectionDigitalClockSection-item': {
                                color: NAVY,
                                fontWeight: 500,
                                borderRadius: RADIUS_ITEM,
                                mx: 0.5,
                              },
                              '& .MuiMultiSectionDigitalClockSection-item:hover': {
                                backgroundColor: alpha(NAVY, 0.06),
                              },
                              '& .MuiMultiSectionDigitalClockSection-item.Mui-selected, & .MuiMultiSectionDigitalClockSection-item.Mui-selected:focus':
                                {
                                  backgroundColor: `${NAVY} !important`,
                                  color: '#fff !important',
                                  fontWeight: 700,
                                },
                              '& .MuiMultiSectionDigitalClockSection-item.Mui-selected:hover': {
                                backgroundColor: `${NAVY} !important`,
                                opacity: 0.92,
                              },
                              '& .MuiPickersToolbar-root': {
                                color: NAVY,
                                '& .MuiTypography-root': { color: NAVY, fontFamily: 'Jost, sans-serif' },
                                '& .MuiTypography-overline': {
                                  color: alpha(NAVY, 0.5),
                                  fontWeight: 600,
                                  letterSpacing: '0.05em',
                                },
                              },
                              '& .MuiDialogActions-root': { backgroundColor: 'transparent', px: 2, py: 1.5, gap: 1 },
                              '& .MuiDialogActions-root .MuiButton-root': {
                                textTransform: 'none',
                                borderRadius: RADIUS_COIN,
                                fontWeight: 600,
                                px: 2.5,
                                fontFamily: 'Jost, sans-serif',
                              },
                              '& .MuiDialogActions-root .MuiButton-root:not(:last-of-type)': {
                                color: NAVY,
                                '&:hover': { backgroundColor: alpha(NAVY, 0.06) },
                              },
                              '& .MuiDialogActions-root .MuiButton-root:last-of-type': {
                                color: NAVY,
                                backgroundColor: YELLOW,
                                boxShadow: 'none',
                                '&:hover': { backgroundColor: YELLOW, opacity: 0.92, boxShadow: SHADOW },
                              },
                            },
                          },
                        }}
                      />
                    </LocalizationProvider>
                  </Box>
                )}
                {ai && (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={aiStatus === 'enabled'}
                        onChange={(e) => setAiStatus(e.target.checked ? 'enabled' : 'disabled')}
                        sx={{
                          color: alpha(NAVY, 0.4),
                          borderRadius: RADIUS_ITEM,
                          '&.Mui-checked': { color: NAVY },
                          '&:hover': { backgroundColor: alpha(NAVY, 0.04) },
                        }}
                      />
                    }
                    label="Respuestas automáticas con IA"
                    sx={{ m: 0, '& .MuiFormControlLabel-label': { color: NAVY, fontSize: '0.9rem', fontWeight: 500 } }}
                  />
                )}
              </Stack>

              {showSubmitButton && selectedTemplate && (
                <Button
                  type="submit"
                  size="large"
                  disabled={!canSend}
                  sx={{
                    mt: 'auto',
                    borderRadius: RADIUS_COIN,
                    textTransform: 'none',
                    fontSize: '1rem',
                    fontWeight: 600,
                    backgroundColor: YELLOW,
                    color: NAVY,
                    boxShadow: 'none',
                    py: 1.25,
                    '&:hover': { backgroundColor: YELLOW, opacity: 0.9, boxShadow: SHADOW },
                    '&.Mui-disabled': { backgroundColor: alpha(NAVY, 0.08), color: alpha(NAVY, 0.3) },
                  }}
                >
                  {schedule ? scheduleLabel : sendLabel}
                </Button>
              )}
            </Box>

            <Box
              sx={{
                flex: 1.5,
                display: 'flex',
                flexDirection: 'column',
                minHeight: isScroll ? panelMinHeight : 0,
              }}
            >
              {selectedTemplate && (
                <Stack spacing={1.5} sx={{ flex: 1, minHeight: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: NAVY }}>
                      Mensaje a enviar:
                    </Typography>
                    {showDelete && (
                      <Button
                        size="small"
                        disabled={isDeleting || isRequestingDeletion}
                        onClick={() => setIsConfirmDeleteOpen(true)}
                        startIcon={
                          isDeleting || isRequestingDeletion ? (
                            <CircularProgress size={14} sx={{ color: '#E7233B' }} />
                          ) : (
                            <DeleteIcon fontSize="small" />
                          )
                        }
                        sx={{
                          color: '#E7233B',
                          textTransform: 'none',
                          fontWeight: 600,
                          borderRadius: RADIUS_COIN,
                          px: 1.5,
                          py: 0.5,
                          flexShrink: 0,
                          '&:hover': { backgroundColor: alpha('#E7233B', 0.08) },
                          '&.Mui-disabled': { color: alpha('#E7233B', 0.35) },
                        }}
                      >
                        {isDeleting ? 'Borrando…' : isRequestingDeletion ? 'Creando solicitud…' : 'Borrar plantilla'}
                      </Button>
                    )}
                  </Box>
                  <TemplatePreview template={selectedTemplate} message={message} />
                </Stack>
              )}
              {!selectedTemplate && (
                <Box
                  sx={{
                    flex: 1,
                    minHeight: 240,
                    backgroundColor: SURFACE_CONTAINER_LOW,
                    borderRadius: RADIUS_CARD,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    p: 3,
                  }}
                >
                  <Typography variant="body2" sx={{ color: alpha(NAVY, 0.5), textAlign: 'center' }}>
                    {templates && templates.length === 0
                      ? 'No hay plantillas disponibles. Pídele a tu administrador que arme una plantilla.'
                      : 'Selecciona una plantilla para ver el mensaje a enviar.'}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        )}

        <Dialog
          open={isConfirmDeleteOpen}
          onClose={() => setIsConfirmDeleteOpen(false)}
          slotProps={{
            paper: {
              sx: {
                backgroundColor: GLASS_BACKGROUND,
                backdropFilter: GLASS_BLUR,
                borderRadius: RADIUS_CARD,
                boxShadow: SHADOW,
                border: `1px solid ${alpha(NAVY, 0.06)}`,
              },
            },
          }}
        >
          <DialogTitle sx={{ color: NAVY, fontWeight: 700 }}>¿Borrar esta plantilla?</DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ color: alpha(NAVY, 0.7) }}>
              {isAdmin
                ? 'Esta acción puede romper algún flujo que dependa de esta plantilla.'
                : 'Tu equipo recibirá una solicitud para eliminarla.'}
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
            <Button
              onClick={() => setIsConfirmDeleteOpen(false)}
              sx={{
                color: NAVY,
                borderRadius: RADIUS_COIN,
                textTransform: 'none',
                fontWeight: 500,
                px: 2.5,
                '&:hover': { backgroundColor: alpha(NAVY, 0.06) },
              }}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleConfirmDelete}
              sx={{
                color: '#E7233B',
                backgroundColor: alpha('#E7233B', 0.08),
                borderRadius: RADIUS_COIN,
                textTransform: 'none',
                fontWeight: 600,
                px: 2.5,
                '&:hover': { backgroundColor: alpha('#E7233B', 0.14) },
              }}
            >
              Sí, borrar
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }
);

TemplateComposer.displayName = 'TemplateComposer';

export default TemplateComposer;
